(function () {
  'use strict';

  // Unique tag: Dawn's global.js already defines <product-recommendations>,
  // so we must NOT reuse that name (its handler would hijack our element).
  if (customElements.get('cz-product-recommendations')) return;

  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this._loaded = false;
      this._observer = null;
    }

    connectedCallback() {
      if (this._loaded || this.dataset.loaded === 'true') return;
      const url = this.dataset.url;
      if (!url) {
        this.remove();
        return;
      }
      this._observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this._disconnectObserver();
            this._load(url);
          }
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this._observer.observe(this);
    }

    disconnectedCallback() {
      this._disconnectObserver();
    }

    _disconnectObserver() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    async _load(url) {
      this._loaded = true;
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const inner = doc.querySelector('.product-recommendations__inner');
        if (!inner || !inner.querySelector('li')) {
          this.remove();
          return;
        }
        this.innerHTML = inner.outerHTML;
        this.dataset.loaded = 'true';
        this.hidden = false;

        const track = this.querySelector('.cz-carousel__track');
        if (!track) return;

        // Self-contained carousel: own arrows + progress + seamless loop.
        // No Dawn <slider-component>, no shared home engine — robust inside
        // the AJAX-injected drawer. Init only once the track has layout
        // (an injected element gets no window.load/resize to retry on).
        const start = () => this._initCarousel(track);
        if (track.clientWidth > 0) {
          requestAnimationFrame(start);
        } else if ('ResizeObserver' in window) {
          const ro = new ResizeObserver(() => {
            if (track.clientWidth > 0) {
              ro.disconnect();
              requestAnimationFrame(start);
            }
          });
          ro.observe(track);
        } else {
          let tries = 0;
          const poll = () => {
            if (track.clientWidth > 0 || tries++ > 60) {
              start();
              return;
            }
            requestAnimationFrame(poll);
          };
          poll();
        }
      } catch (e) {
        console.error('[product-recommendations]', e);
        this.remove();
      }
    }

    _initCarousel(track) {
      if (track.dataset.czRecoReady) return;
      track.dataset.czRecoReady = '1';

      const originals = Array.from(track.querySelectorAll('.cz-carousel__slide'));
      const N = originals.length;
      const bar = this.querySelector('[data-cz-progress]');
      const counterCur = this.querySelector('.slider-counter--current');
      const counterTot = this.querySelector('.slider-counter--total');
      const prevBtn = this.querySelector('button[name="previous"]');
      const nextBtn = this.querySelector('button[name="next"]');

      if (counterTot) counterTot.textContent = N;

      if (N < 2) {
        if (prevBtn) prevBtn.hidden = true;
        if (nextBtn) nextBtn.hidden = true;
        if (bar) {
          bar.style.width = '100%';
          bar.style.transform = 'translateX(0)';
        }
        return;
      }

      // Clone the full set before and after for a seamless infinite loop.
      const makeClone = (src) => {
        const c = src.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.classList.add('cz-carousel__slide--clone');
        c.removeAttribute('id');
        c.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        c.querySelectorAll('a').forEach((el) => el.setAttribute('tabindex', '-1'));
        return c;
      };
      for (let i = 0; i < N; i++) track.appendChild(makeClone(originals[i]));
      for (let i = N - 1; i >= 0; i--) track.insertBefore(makeClone(originals[i]), track.firstChild);

      // Scroll offset of a slide within the track, padding/offsetParent-safe.
      const slideLeft = (el) =>
        el.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;

      let step = 0;
      let firstRealLeft = 0;

      const setScroll = (x) => {
        const sb = track.style.scrollBehavior;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = x;
        track.style.scrollBehavior = sb;
      };

      const measure = () => {
        step = Math.round(slideLeft(originals[1]) - slideLeft(originals[0]));
        firstRealLeft = slideLeft(originals[0]);
      };

      const logicalIndex = () => {
        if (step <= 0) return 0;
        const p = Math.round((track.scrollLeft - firstRealLeft) / step);
        return ((p % N) + N) % N;
      };

      const updateProgress = () => {
        const idx = logicalIndex();
        if (bar) {
          // Fill from (1/N) up to 100% across the set, then loop resets it.
          bar.style.width = ((idx + 1) / N) * 100 + '%';
          bar.style.transform = 'translateX(0)';
        }
        if (counterCur) counterCur.textContent = idx + 1;
      };

      const checkWrap = () => {
        if (step <= 0) return;
        const p = Math.round((track.scrollLeft - firstRealLeft) / step);
        if (p < 0) {
          setScroll(track.scrollLeft + N * step);
        } else if (p >= N) {
          setScroll(track.scrollLeft - N * step);
        }
        updateProgress();
      };

      const goReady = () => {
        measure();
        if (step <= 0) {
          requestAnimationFrame(goReady);
          return;
        }
        setScroll(firstRealLeft);
        updateProgress();

        if (nextBtn) {
          nextBtn.hidden = false;
          nextBtn.addEventListener('click', () => {
            track.scrollBy({ left: step, behavior: 'smooth' });
          });
        }
        if (prevBtn) {
          prevBtn.hidden = false;
          prevBtn.addEventListener('click', () => {
            track.scrollBy({ left: -step, behavior: 'smooth' });
          });
        }

        let rafPending = false;
        let wrapTimer;
        track.addEventListener(
          'scroll',
          () => {
            if (!rafPending) {
              rafPending = true;
              requestAnimationFrame(() => {
                rafPending = false;
                updateProgress();
              });
            }
            clearTimeout(wrapTimer);
            wrapTimer = setTimeout(checkWrap, 140);
          },
          { passive: true }
        );

        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            const idx = logicalIndex();
            measure();
            if (step > 0) {
              setScroll(firstRealLeft + idx * step);
              updateProgress();
            }
          }, 120);
        });
      };

      goReady();
    }
  }

  customElements.define('cz-product-recommendations', ProductRecommendations);
})();
